import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import MobileDatePicker from '../components/MobileDatePicker.tsx';
import { format } from 'date-fns';
import { managementAPI, pendingJobAPI, assignmentAPI } from '../services/api.ts';

interface ManagerHourItem {
  loginId: string;
  name: string;
  totalJobHour: number; // total hours for the day range
  totalCompletedJobBefore5Pm: number;
  totalCompletedJobAfter5Pm: number;
}

const ManagerManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlStart = searchParams.get('start') || '';
  const urlEnd = searchParams.get('end') || '';
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const defaultStart = format(firstDayOfMonth, 'yyyy-MM-dd');
  const defaultEnd = format(today, 'yyyy-MM-dd');

  const [startDate, setStartDate] = useState<string>(urlStart || defaultStart);
  const [endDate, setEndDate] = useState<string>(urlEnd || defaultEnd);
  const [items, setItems] = useState<ManagerHourItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [startDateObj, setStartDateObj] = useState<Date | null>(startDate ? new Date(startDate) : null);
  const [endDateObj, setEndDateObj] = useState<Date | null>(endDate ? new Date(endDate) : null);
  const [actionsOpenId, setActionsOpenId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignManagers, setAssignManagers] = useState<string[]>([]);
  const [pendingJobs, setPendingJobs] = useState<any[]>([]);
  const [pendingJobsLoading, setPendingJobsLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [pendingJobsLoadingMore, setPendingJobsLoadingMore] = useState(false);
  const [pendingJobsPage, setPendingJobsPage] = useState(1);
  const [pendingJobsHasMore, setPendingJobsHasMore] = useState(true);
  const [managerFilter, setManagerFilter] = useState('');

  useEffect(() => {
    if (startDate && endDate) {
      setSearchParams({ start: startDate, end: endDate });
    }
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await managementAPI.getManagerListing({
        StartDate: startDate,
        EndDate: endDate
      });
      const container = response?.data ?? response;
      const list: any[] = Array.isArray(container) ? container : Array.isArray(container?.data) ? container.data : [];
      const mapped: ManagerHourItem[] = list.map((x: any) => {
        const beforeJobs = Number(x.totalCompletedJobBefore5Pm ?? x.TotalCompletedJobBefore5Pm ?? 0);
        const afterJobs = Number(x.totalCompletedJobAfter5Pm ?? x.TotalCompletedJobAfter5Pm ?? 0);
        const totalJobHour = Number(x.totalJobHour ?? x.TotalJobHour ?? 0);
        return {
          loginId: String(x.loginId ?? x.LoginId ?? ''),
          name: x.name ?? x.Name ?? 'Unknown',
          totalJobHour,
          totalCompletedJobBefore5Pm: beforeJobs,
          totalCompletedJobAfter5Pm: afterJobs,
        };
      });
      setItems(mapped);
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    fetchData();
  };

  const formatDateForDisplay = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  const handleStartDateSelect = (date: Date | null) => {
    setStartDateObj(date);
    if (date) {
      setStartDate(format(date, 'yyyy-MM-dd'));
    } else {
      setStartDate('');
    }
    setShowStartDatePicker(false);
  };

  const handleEndDateSelect = (date: Date | null) => {
    setEndDateObj(date);
    if (date) {
      setEndDate(format(date, 'yyyy-MM-dd'));
    } else {
      setEndDate('');
    }
    setShowEndDatePicker(false);
  };

  // Removed Assign Job action per request

  const isAdmin = user?.position === 4;

  return (
    <div className="container-app py-6">
      {!isAdmin && (
        <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded p-3 mb-4">
          You do not have permission to view this page.
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Start Date</label>
            <div 
              className="input flex items-center cursor-pointer"
              onClick={() => setShowStartDatePicker(true)}
            >
              <input
                type="text"
                readOnly
                className="flex-grow cursor-pointer bg-transparent border-0 p-0 focus:outline-none"
                value={formatDateForDisplay(startDateObj)}
                placeholder="Select start date"
              />
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">End Date</label>
            <div 
              className="input flex items-center cursor-pointer"
              onClick={() => setShowEndDatePicker(true)}
            >
              <input
                type="text"
                readOnly
                className="flex-grow cursor-pointer bg-transparent border-0 p-0 focus:outline-none"
                value={formatDateForDisplay(endDateObj)}
                placeholder="Select end date"
              />
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div className="flex items-end">
            <button type="submit" className="btn btn-primary w-full">Search</button>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Working Hours</h2>
            <p className="text-xs text-gray-500">From {startDate || '-'} to {endDate || '-'}</p>
          </div>
          {selectedManagers.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={async () => {
                setAssignManagers(selectedManagers);
                setAssignError(null);
                setShowAssignModal(true);
                try {
                  setPendingJobsLoading(true);
                  const res = await pendingJobAPI.getPendingJobs({ StartDate: startDate, EndDate: endDate, PageNumber: 1, PageSize: 50 });
                  const list = Array.isArray(res?.data) ? res.data : res;
                  setPendingJobs(Array.isArray(list) ? list : []);
                } catch (e: any) {
                  setAssignError(e.message || 'Failed to load pending jobs');
                } finally {
                  setPendingJobsLoading(false);
                }
              }}
            >
              Assign Jobs ({selectedManagers.length})
            </button>
          )}
        </div>
        {loading ? (
          <div className="p-6 flex justify-center items-center" style={{ minHeight: 96 }}>
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-red-600">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-sm text-gray-600 border-b">
                  <th className="p-3">Login ID</th>
                  <th className="p-3">Manager</th>
                  <th className="p-3">Jobs ≤5pm</th>
                  <th className="p-3">Jobs &gt;5pm</th>
                  <th className="p-3">Total Hours</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr
                    key={item.loginId}
                    className={`border-b cursor-pointer ${selectedManagers.includes(item.loginId) ? 'bg-primary/5' : ''}`}
                    onClick={() => {
                      setSelectedManagers(prev => {
                        const set = new Set(prev);
                        if (set.has(item.loginId)) set.delete(item.loginId); else set.add(item.loginId);
                        return Array.from(set);
                      });
                    }}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {selectedManagers.includes(item.loginId) ? (
                          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="w-4 h-4 inline-block border rounded"></span>
                        )}
                        <span>{item.loginId}</span>
                      </div>
                    </td>
                    <td className="p-3 font-medium">{item.name}</td>
                    <td className="p-3">{item.totalCompletedJobBefore5Pm}</td>
                    <td className="p-3">{item.totalCompletedJobAfter5Pm}</td>
                    <td className="p-3 font-medium">{item.totalJobHour.toFixed(2)}</td>
                    <td className="p-3">
                      <div className="relative inline-block text-left">
                        <button
                          className="px-2 py-1 rounded hover:bg-gray-100"
                          onClick={(e) => { e.stopPropagation(); setActionsOpenId(prev => prev === item.loginId ? null : item.loginId); }}
                          aria-haspopup="true"
                          aria-expanded={actionsOpenId === item.loginId}
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                        </button>
                        {actionsOpenId === item.loginId && (
                          <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg z-10 border">
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                              onClick={() => {
                                setActionsOpenId(null);
                                navigate(`/manager-history?loginId=${encodeURIComponent(item.loginId)}&start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`)
                              }}
                            >
                              View History
                            </button>
                            <button
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                              onClick={async () => {
                                setActionsOpenId(null);
                                setAssignError(null);
                                setAssignManagers([item.loginId]);
                                setShowAssignModal(true);
                                try {
                                  setPendingJobsLoading(true);
                                  const PAGE_SIZE = 10;
                                  const res = await pendingJobAPI.getPendingJobs({ StartDate: startDate, EndDate: endDate, PageNumber: 1, PageSize: PAGE_SIZE });
                                  const list = Array.isArray(res?.data) ? res.data : res;
                                  const normalized = Array.isArray(list) ? list : [];
                                  setPendingJobs(normalized);
                                  setPendingJobsPage(1);
                                  setPendingJobsHasMore(normalized.length === PAGE_SIZE);
                                } catch (e: any) {
                                  setAssignError(e.message || 'Failed to load pending jobs');
                                } finally {
                                  setPendingJobsLoading(false);
                                }
                              }}
                            >
                              Assign Jobs
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td className="p-3 text-gray-500" colSpan={6}>No data</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Mobile Date Pickers */}
      <MobileDatePicker
        isOpen={showStartDatePicker}
        onClose={() => setShowStartDatePicker(false)}
        onSelectDate={handleStartDateSelect}
        selectedDate={startDateObj}
        initialDate={startDateObj || new Date()}
      />
      <MobileDatePicker
        isOpen={showEndDatePicker}
        onClose={() => setShowEndDatePicker(false)}
        onSelectDate={handleEndDateSelect}
        selectedDate={endDateObj}
        initialDate={endDateObj || new Date()}
      />

      {/* Assign Jobs Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn pb-safe">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp mb-16 md:mb-4">
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Assign Jobs</h2>
              <button 
                onClick={() => setShowAssignModal(false)}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-gray-600">Select Managers</label>
                  <div className="space-x-2">
                    <button
                      type="button"
                      className="text-xs text-primary"
                      onClick={() => setAssignManagers(items.map(i => i.loginId))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-600"
                      onClick={() => setAssignManagers([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  className="input mb-2"
                  placeholder="Search login id or name..."
                  value={managerFilter}
                  onChange={(e) => setManagerFilter(e.target.value)}
                />
                <div className="max-h-40 overflow-auto border rounded divide-y mb-2">
                  {items
                    .filter(m =>
                      managerFilter.trim() === '' ||
                      m.loginId.toLowerCase().includes(managerFilter.toLowerCase()) ||
                      m.name.toLowerCase().includes(managerFilter.toLowerCase())
                    )
                    .map(m => {
                      const selected = assignManagers.includes(m.loginId);
                      return (
                        <button
                          key={m.loginId}
                          type="button"
                          onClick={() => {
                            setAssignManagers(prev => {
                              const set = new Set(prev);
                              if (selected) set.delete(m.loginId); else set.add(m.loginId);
                              return Array.from(set);
                            });
                          }}
                          className={`w-full text-left p-3 flex items-center justify-between ${selected ? 'bg-primary/5 border-l-4 border-primary' : ''}`}
                        >
                          <span className="text-sm">{m.loginId} - {m.name}</span>
                          {selected && (
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {assignManagers.map(m => {
                    const mgr = items.find(i => i.loginId === m);
                    const label = mgr ? `${mgr.loginId} - ${mgr.name}` : m;
                    return (
                      <span key={m} className="px-2 py-1 bg-gray-100 rounded text-sm">{label}</span>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Pending Jobs</label>
                {pendingJobsLoading ? (
                  <div className="p-6 flex justify-center items-center" style={{ minHeight: 96 }}>
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
                  </div>
                ) : assignError ? (
                  <div className="text-red-600 text-sm">{assignError}</div>
                ) : (
                  <div
                    className="max-h-64 overflow-auto border rounded divide-y"
                    onScroll={async (e) => {
                      const el = e.currentTarget as HTMLDivElement;
                      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 32;
                      if (nearBottom && !pendingJobsLoadingMore && pendingJobsHasMore) {
                        try {
                          setPendingJobsLoadingMore(true);
                          const nextPage = pendingJobsPage + 1;
                          const PAGE_SIZE = 10;
                          const res = await pendingJobAPI.getPendingJobs({ StartDate: startDate, EndDate: endDate, PageNumber: nextPage, PageSize: PAGE_SIZE });
                          const list = Array.isArray(res?.data) ? res.data : res;
                          const normalized = Array.isArray(list) ? list : [];
                          setPendingJobs(prev => [...prev, ...normalized]);
                          setPendingJobsPage(nextPage);
                          setPendingJobsHasMore(normalized.length === PAGE_SIZE);
                        } catch (err) {
                          // ignore infinite scroll error
                        } finally {
                          setPendingJobsLoadingMore(false);
                        }
                      }
                    }}
                  >
                    {pendingJobs.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500">No pending jobs in this range.</div>
                    ) : (
                      <>
                        {pendingJobs.map((job: any) => (
                          <label key={job.id ?? job.Id} className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={selectedJobIds.includes(String(job.id ?? job.Id))}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const id = String(job.id ?? job.Id);
                                setSelectedJobIds(prev => {
                                  const set = new Set(prev);
                                  if (checked) set.add(id); else set.delete(id);
                                  return Array.from(set);
                                });
                              }}
                            />
                            <div className="text-sm">
                              <div className="font-medium text-gray-800">{job.projectName ?? job.location ?? 'Job'}</div>
                              <div className="text-gray-600">{job.date} • {job.time}</div>
                              {job.address && <div className="text-xs text-gray-500">{job.address}</div>}
                            </div>
                          </label>
                        ))}
                        {pendingJobsLoadingMore && (
                          <div className="p-3 flex justify-center items-center">
                            <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div>
                          </div>
                        )}
                        {!pendingJobsHasMore && pendingJobs.length > 0 && (
                          <div className="p-3 text-center text-xs text-gray-500">No more jobs</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-gray-200 p-4">
              <button
                className={`btn w-full ${selectedJobIds.length === 0 || assignManagers.length === 0 ? 'btn-disabled' : 'btn-primary'}`}
                disabled={selectedJobIds.length === 0 || assignManagers.length === 0}
                onClick={async () => {
                  if (selectedJobIds.length === 0 || assignManagers.length === 0) return;
                  try {
                    // submit one by one for clarity; backend expects single ScheduleId per call
                    for (const scheduleId of selectedJobIds) {
                      const res = await assignmentAPI.updateScheduleManager({
                        ManagerId: assignManagers,
                        ScheduleId: Number(scheduleId)
                      });
                      if (res.status !== 200) {
                        throw new Error('Failed to update some schedules');
                      }
                    }
                    // Success UI feedback
                    setShowAssignModal(false);
                    setSelectedJobIds([]);
                  } catch (e: any) {
                    setAssignError(e.message || 'Failed to assign jobs');
                  }
                }}
              >
                Assign Selected ({selectedJobIds.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerManagement;


